import React, { useState, useEffect } from 'react';
import { getEpAdminGroupList } from '../../services/EP/EpAdminService';
import { IGroupInfo } from '../../interfaces/EpModal';

interface Group {
  ApplyNo: string;
  SchoolName: string;
  City: string;
  ApplyName: string;
  GroupNo: string;
  GroupName: string;
  TeacherList: string;
  TeacherCount: number;
  StudentList: string;
  StudentCount: string;
}

const EpAdmin: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null); // 預設全部縮起來

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const result = await getEpAdminGroupList() as Group[];
      setGroups(result);
    } catch (ex) {
      alert(JSON.stringify(ex));
    }
  };

  const toggleExpand = (groupNo: string) => {
    setExpandedGroup(expandedGroup === groupNo ? null : groupNo);
  };

  // ➤ 將 groups 依照 SchoolName 分群
  const groupedBySchool: Record<string, Group[]> = {};
  groups.forEach((group) => {
    const school = group.SchoolName?.trim() || '未知學校';
    if (!groupedBySchool[school]) {
      groupedBySchool[school] = [];
    }
    groupedBySchool[school].push(group);
  });

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">小隊管理後台</h1>

      {/* 依學校顯示 */}
      {Object.entries(groupedBySchool).map(([schoolName, groupList]) => (
        <div key={schoolName} className="mb-10">
          {/* 學校名稱 */}
          <h2 className="text-2xl font-bold text-blue-800 mb-4 border-b pb-2">
            📚 {schoolName}
          </h2>

          {/* 該學校的小隊清單 */}
          <table className="w-full text-sm border border-gray-300 mb-6 bg-white rounded shadow">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="p-2 w-8">#</th>
                <th className="p-2">小隊名稱</th>
                <th className="p-2">小隊編號</th>
                <th className="p-2">城市</th>
                <th className="p-2">負責人</th>
                <th className="p-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {groupList.map((group, index) => (
                <React.Fragment key={group.GroupNo}>
                  <tr className="border-t">
                    <td className="p-2 text-center">{index + 1}</td>
                    <td className="p-2">{group.GroupName.trim()}</td>
                    <td className="p-2">{group.GroupNo}</td>
                    <td className="p-2">{group.City || '－'}</td>
                    <td className="p-2">
                      {group.ApplyName.trim()} ({group.ApplyNo})
                    </td>
                    <td className="p-2">
                      <button
                        className="text-blue-600 hover:underline"
                        onClick={() => toggleExpand(group.GroupNo)}
                      >
                        {expandedGroup === group.GroupNo ? ' ▲' : ' ▼'}
                      </button>
                    </td>
                  </tr>

                  {/* 展開內容 */}
                  {expandedGroup === group.GroupNo && (
                    <tr className="bg-gray-50 border-t">
                      <td colSpan={6} className="p-4">
                        {/* 老師清單 */}
                        <div className="mb-4">
                          <h4 className="font-bold mb-2 text-gray-700">
                            👩‍🏫 老師清單（{group.TeacherCount} 位）
                          </h4>
                          <table className="w-full border border-gray-300 text-sm mb-2">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="p-2 text-left">編號</th>
                                <th className="p-2 text-left">姓名</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.TeacherList?.trim() ? (
                                group.TeacherList.split(',').map((item, index) => {
                                  const [code = '', name = ''] = item
                                    .trim()
                                    .split('-')
                                    .map((s) => s.trim());
                                  return (
                                    <tr key={index} className="border-t">
                                      <td className="p-2">{code}</td>
                                      <td className="p-2">{name}</td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={2} className="p-2 text-gray-500">
                                    無老師資料
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/* 學生清單 */}
                        <div>
                          <h4 className="font-bold mb-2 text-gray-700">
                            👨‍🎓 學生清單（{group.StudentCount} 人）
                          </h4>
                          <table className="w-full border border-gray-300 text-sm">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="p-2 text-left">編號</th>
                                <th className="p-2 text-left">姓名</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.StudentList?.trim() ? (
                                group.StudentList.split(',').map((item, index) => {
                                  const [code = '', name = ''] = item
                                    .trim()
                                    .split('-')
                                    .map((s) => s.trim());
                                  return (
                                    <tr key={index} className="border-t">
                                      <td className="p-2">{code}</td>
                                      <td className="p-2">{name}</td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={2} className="p-2 text-gray-500">
                                    無學生資料
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default EpAdmin;
